import type { PortfolioCompletion } from "./types";
import type { MemberDutyConfiguration, UpdateMemberDutyConfiguration } from "./types";
import type { DutyHistory, DutyHistoryOptions } from "../features/duty-history/types";
import type { OrganizationApplication } from "./types";
import { collectTaskPages } from "./taskPagination";
import type { WorkspaceTaskFilters, WorkspaceTaskPage } from "./types";
import type { LeaveCalendarEntry, LeaveListing, LeaveOptions, LeaveRequest, LeaveStatus } from "../features/leaves/types";
import type { ProjectBilling, SaveProjectBilling } from "../features/projects/projectBilling";
import type { ProjectOverviewResponse } from "../features/projects/projectOverviewTypes";
import {
  ApiFailure,
  ApiSuccess,
  AssetsResponse,
  AssetTagsResponse,
  BlockerReasonsResponse,
  LocationAsset,
  AssetTag,
  CataloguePartnersResponse,
  CatalogueProject,
  CatalogueProjectRowsResponse,
  CatalogueItemsResponse,
  CreateProjectRequestDetail,
  CreateProjectRequestDetailResponse,
  CreateOrganizationResponse,
  DownloadTargetResponse,
  DutyStatusResponse,
  FileUploadDetailsResponse,
  FileUploadInitResponse,
  FieldOpsTaskDetails,
  FieldRoutePlan,
  GoogleMapsConfiguration,
  HomeSummary,
  InternalUsersResponse,
  ListProjectFileExportAvailabilityResponse,
  ListProjectFileExportsResponse,
  ListProjectArtworksResponse,
  ListProjectArtworkMockupsResponse,
  ListCreateProjectRequestsResponse,
  ListProjectReportsResponse,
  ListProjectLocationsResponse,
  ListProjectsResponse,
  LocationGroupResponse,
  LocationGroupsResponse,
  LocationsResponse,
  MasterDataUploadResponse,
  MasterDataUploadsResponse,
  OrgHierarchyLevel,
  OrgLocationAccess,
  OrgLocationDirectUser,
  OrganizationConnection,
  OrganizationConnectionBlock,
  OrganizationConnectionBlocksResponse,
  OrganizationConnectionDiscoveryResponse,
  OrganizationConnectionRequest,
  OrganizationConnectionRequestsResponse,
  OrganizationConnectionsResponse,
  OrganizationRoleCode,
  OrganizationRolesResponse,
  OrganizationHierarchyUser,
  StaffReportingView,
  OrganizationReportingUpdateResponse,
  OrganizationUsersResponse,
  ProjectDetailsResponse,
  ProjectLocationDetails,
  ProjectDownload,
  ProjectArtworkUploadCompleteResponse,
  ProjectArtworkUploadInitResponse,
  ProjectArtworkUploadPartsResponse,
  ProjectArtworkMockupMutationResponse,
  ProjectArtworkMockupUploadInitResponse,
  ProjectCreationSourceType,
  ProjectFileExportResponse,
  ProjectFileExportType,
  ProjectPrinterEstimatesResponse,
  ProjectSummary,
  ProductionBatchItemLineOption,
  ProjectTask,
  ProductionWorkspace,
  RoutePlanningTask,
  RateCardCounterpartiesResponse,
  RateCardDetailResponse,
  RateCardDiscussionResponse,
  RateCardsResponse,
  ProjectTimelineResponse,
  ProjectStatus,
  ProjectsSnapshotResponse,
  RefreshSessionResponse,
  SendInstallationFyiResponse,
  SendRecceReportResponse,
  SendOtpResponse,
  SetOrganizationAffiliationResponse,
  SetRegistrationTypeResponse,
  OrganizationAffiliation,
  OrganizationOnDutyRoleSettingsResponse,
  OrganizationSettingsResponse,
  OrganizationTabPermissionMatrix,
  OwnershipWorkspace,
  TaskComment,
  TaskHistoryEvent,
  RegistrationType,
  UpdateProjectArtworkItemLinesResponse,
  UserContextResponse,
  VendorResourceOptions,
  ComplianceLocationOption,
  BulkInvoiceCommitResult,
  BulkInvoicePreview,
  StoreComplianceSettings,
  StoreComplianceTask,
  StoreManagerProvisioningTask,
  VendorsResponse,
  VerifyOtpResponse,
  OrganizationCommercialProfile,
  OrganizationAddressKind,
} from "./types";
import { getStoredSession, removeStoredSession, storeSession } from "./session";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "https://api.coprint.in/api/v1";

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  auth?: boolean;
  headers?: Record<string, string>;
};

type ProjectCollaborationRequestBody = {
  collaboration_type: "SINGLE_VENDOR" | "MULTI_VENDOR";
  printer_organization_id?: string | null;
  recce_organization_id?: string | null;
  recce_internal_user_id?: string | null;
  recce_source?: string | null;
  designer_organization_id?: string | null;
  designer_internal_user_id?: string | null;
  designer_source?: string | null;
  delivery_installation_required: boolean;
};

type ApiEnvelope<T> = ApiSuccess<T> | ApiFailure;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

let refreshPromise: Promise<string | null> | null = null;

async function parseEnvelope<T>(response: Response): Promise<ApiEnvelope<T>> {
  const text = await response.text();
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    const body = text.trim();
    const backendUnavailable = response.status >= 500
      || body.includes("ECONNREFUSED")
      || body.includes("Error occurred while trying to proxy");
    if (backendUnavailable) {
      throw new ApiError(
        response.status,
        "BACKEND_UNAVAILABLE",
        "Could not reach the Coprint backend. Make sure the backend is running on port 8080."
      );
    }
    throw new ApiError(response.status, "INVALID_API_RESPONSE", "The backend returned a non-JSON response.");
  }
}

async function refreshAccessToken() {
  const session = getStoredSession();
  if (!session?.refreshToken) {
    removeStoredSession();
    return null;
  }
  const response = await fetch(`${API_BASE_URL}/auth/session/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: session.refreshToken })
  });
  const payload = await parseEnvelope<RefreshSessionResponse>(response);
  if (response.ok && payload.success) {
    storeSession({ accessToken: payload.data.access_token, refreshToken: payload.data.refresh_token });
    return payload.data.access_token;
  }
  if (response.status === 401 || response.status === 403) {
    removeStoredSession();
    return null;
  }
  const failure = payload as ApiFailure;
  throw new ApiError(response.status, failure.error.code, failure.error.message);
}

async function sendRequest<T>(path: string, options: RequestOptions, accessToken: string | null) {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: options.method ?? "GET",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body)
    });
  } catch {
    throw new ApiError(0, "NETWORK_UNAVAILABLE", "Could not reach the Coprint backend.");
  }
  return { response, payload: await parseEnvelope<T>(response) };
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const session = getStoredSession();
  const accessToken = options.auth === false ? null : session?.accessToken ?? null;
  const result = await sendRequest<T>(path, options, accessToken);
  if (result.response.ok && result.payload.success) {
    return result.payload.data;
  }
  if (result.response.status === 401 && options.auth !== false && accessToken) {
    refreshPromise ??= refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
    const refreshed = await refreshPromise;
    if (refreshed) {
      const retry = await sendRequest<T>(path, options, refreshed);
      if (retry.response.ok && retry.payload.success) {
        return retry.payload.data;
      }
      const failure = retry.payload as ApiFailure;
      if (failure.error.code === "ORGANIZATION_NOT_LIVE") window.dispatchEvent(new Event("organization-access-changed"));
      throw new ApiError(retry.response.status, failure.error.code, failure.error.message);
    }
  }
  const failure = result.payload as ApiFailure;
  if (failure.error.code === "ORGANIZATION_NOT_LIVE") window.dispatchEvent(new Event("organization-access-changed"));
  throw new ApiError(result.response.status, failure.error.code, failure.error.message);
}

async function apiBlob(path: string): Promise<Blob> {
  const session = getStoredSession();
  let token = session?.accessToken ?? null;
  const send = (accessToken: string | null) => fetch(`${API_BASE_URL}${path}`, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
  });
  let response: Response;
  try {
    response = await send(token);
  } catch {
    throw new ApiError(0, "NETWORK_UNAVAILABLE", "Could not reach the Coprint backend.");
  }
  if (response.status === 401 && token) {
    refreshPromise ??= refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
    token = await refreshPromise;
    if (token) response = await send(token);
  }
  if (!response.ok) {
    const payload = await parseEnvelope<never>(response);
    const failure = payload as ApiFailure;
    throw new ApiError(response.status, failure.error.code, failure.error.message);
  }
  return response.blob();
}

export const api = {
  getMemberDutySettings(userId: string) {
    return apiRequest<MemberDutyConfiguration>(`/organization-users/${encodeURIComponent(userId)}/duty-settings`);
  },
  updateMemberDutySettings(userId: string, body: UpdateMemberDutyConfiguration) {
    return apiRequest<MemberDutyConfiguration>(`/organization-users/${encodeURIComponent(userId)}/duty-settings`, { method: "PUT", body });
  },
  listUnbilledProjects() { return apiRequest<ProjectBilling[]>("/projects/unbilled"); },
  getProjectBilling(projectId: string) { return apiRequest<ProjectBilling>(`/projects/${projectId}/billing`); },
  saveProjectBilling(projectId: string, body: SaveProjectBilling) {
    return apiRequest<ProjectBilling>(`/projects/${projectId}/billing`, { method: "PUT", body });
  },
  sendOtp(mobileNumber: string) {
    return apiRequest<SendOtpResponse>("/auth/otp/send", {
      method: "POST",
      auth: false,
      body: { mobile_number: mobileNumber }
    });
  },
  verifyOtp(otpRequestId: string, mobileNumber: string, otp: string) {
    return apiRequest<VerifyOtpResponse>("/auth/otp/verify", {
      method: "POST",
      auth: false,
      body: { otp_request_id: otpRequestId, mobile_number: mobileNumber, otp }
    });
  },
  getUserContext() {
    return apiRequest<UserContextResponse>("/users/me/context");
  },
  switchActiveOrganization(organizationId: string) {
    return apiRequest<UserContextResponse>("/users/me/active-organization", {
      method: "PUT",
      body: { organization_id: organizationId }
    });
  },
  getCompletionSummary() {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
    return apiRequest<PortfolioCompletion>(`/projects/completion-summary?timeZone=${encodeURIComponent(zone)}`);
  },
  getHomeSummary() {
    return apiRequest<HomeSummary>("/agency/home-summary");
  },
  getDutyStatus() {
    return apiRequest<DutyStatusResponse>("/duty-status");
  },
  getDutyHistoryOptions() {
    return apiRequest<DutyHistoryOptions>("/duty-status/history/options");
  },
  getDutyHistory(userId: string, date: string) {
    return apiRequest<DutyHistory>(`/duty-status/history?${new URLSearchParams({ user_id: userId, date })}`);
  },
  setRegistrationType(registrationType: RegistrationType, profile?: { name: string; email: string }) {
    return apiRequest<SetRegistrationTypeResponse>("/users/me/registration-type", {
      method: "PUT",
      body: { registration_type: registrationType, ...profile }
    });
  },
  setOrganizationAffiliation(organizationAffiliation: OrganizationAffiliation) {
    return apiRequest<SetOrganizationAffiliationResponse>("/users/me/organization-affiliation", {
      method: "PUT",
      body: { organization_affiliation: organizationAffiliation }
    });
  },
  createOrganization(application: OrganizationApplication) {
    return apiRequest<CreateOrganizationResponse>("/organizations", {
      method: "POST",
      body: application
    });
  },
  listOrganizationRoles() {
    return apiRequest<OrganizationRolesResponse>("/organization-roles");
  },
  listOrganizationUsers() {
    return apiRequest<OrganizationUsersResponse>("/organization-users");
  },
  updateOrganizationUserOnDutyTracking(userId: string, enabled: boolean) {
    return apiRequest<OrganizationUsersResponse[number]>(`/organization-users/${userId}/on-duty-tracking`, {
      method: "PUT",
      body: { enabled }
    });
  },
  listProductionAssignees() {
    return apiRequest<OrganizationUsersResponse>("/vendor/production-assignees");
  },
  getOwnershipWorkspace(view: "live" | "completed") {
    return apiRequest<OwnershipWorkspace>(`/ownership?view=${view}`);
  },
  assignOwnershipManagers(itemType: "PROJECT", itemId: string, body: {
    project_manager_id?: string | null;
    production_manager_id?: string | null;
    delivery_manager_id?: string | null;
  }) {
    return apiRequest<OwnershipWorkspace["items"][number]>(`/ownership/${itemType}/${itemId}/managers`, {
      method: "PUT",
      body
    });
  },
  getProductionWorkspace(params?: {
    taskType?: "PRODUCTION" | "PACKAGING" | "DELIVERY" | null;
    sourceType?: "PROJECT" | null;
    sourceId?: string | null;
    page?: number;
    size?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.taskType) {
      query.set("task_type", params.taskType);
    }
    if (params?.sourceType && params.sourceId) {
      query.set("source_type", params.sourceType);
      query.set("source_id", params.sourceId);
    }
    if (params?.page != null) query.set("page", String(params.page));
    if (params?.size != null) query.set("size", String(params.size));
    return apiRequest<ProductionWorkspace>(`/production-workspace${query.size ? `?${query}` : ""}`);
  },
  splitProductionTask(taskId: string, items: Array<{
    source_item_id: string;
    original_task_quantity: number;
    new_task_quantity: number;
  }>) {
    return apiRequest<{ original_task_id: string; new_task_id: string }>(`/production-workspace/tasks/${taskId}/split`, {
      method: "POST",
      body: { items }
    });
  },
  createManualProductionTask(body: {
    context_task_id: string;
    title: string;
    description?: string | null;
    item_code?: string | null;
    quantity?: number | null;
  }) {
    return apiRequest<{ task_id: string }>("/production-workspace/tasks/manual", {
      method: "POST",
      body
    });
  },
  assignProductionTask(taskId: string, assigneeId: string | null) {
    return apiRequest<{ task_id: string }>(`/production-workspace/tasks/${taskId}/assign`, {
      method: "POST",
      body: { assignee_id: assigneeId }
    });
  },
  startProductionWorkspaceTask(taskId: string) {
    return apiRequest<{ task_id: string }>(`/production-workspace/tasks/${taskId}/start`, { method: "POST" });
  },
  completeProductionWorkspaceTask(taskId: string) {
    return apiRequest<{ task_id: string }>(`/production-workspace/tasks/${taskId}/complete`, { method: "POST" });
  },
  updateProductionWorkspaceTaskStatus(taskId: string, status: "IN_PROGRESS" | "COMPLETED") {
    return apiRequest<{ task_id: string }>(`/production-workspace/tasks/${taskId}/status`, {
      method: "POST",
      body: { status }
    });
  },
  blockProductionWorkspaceTask(taskId: string, body: { reason_id: string; comment?: string | null }) {
    return apiRequest<{ task_id: string }>(`/production-workspace/tasks/${taskId}/block`, {
      method: "POST",
      body
    });
  },
  unblockProductionWorkspaceTask(taskId: string, body: { comment?: string | null } = {}) {
    return apiRequest<{ task_id: string }>(`/production-workspace/tasks/${taskId}/unblock`, {
      method: "POST",
      body
    });
  },
  changeProductionWorkspaceTaskPriority(taskId: string, body: {
    target_task_id: string;
    placement: "ABOVE" | "BELOW";
  }) {
    return apiRequest<{ task_id: string }>(`/production-workspace/tasks/${taskId}/priority`, {
      method: "POST",
      body
    });
  },
  downloadProductionDeliveryChallan(deliveryId: string) {
    return apiBlob(`/production-workspace/deliveries/${deliveryId}/delivery-challan`);
  },
  downloadProductionPackingSlip(deliveryId: string) {
    return apiBlob(`/production-workspace/deliveries/${deliveryId}/packing-slip`);
  },
  createOrganizationUser(body: {
    name: string;
    email: string;
    mobile_number: string;
    role_code: OrganizationRoleCode;
    team_id?: string | null;
  }) {
    return apiRequest<OrganizationUsersResponse[number]>("/organization-users", {
      method: "POST",
      body
    });
  },
  getStaffReporting() {
    return apiRequest<StaffReportingView>("/staff-reporting");
  },
  getLeaveOptions() { return apiRequest<LeaveOptions>("/leave-requests/options"); },
  getLeaveCalendar(from: string, to: string) {
    const query = new URLSearchParams({ from_date: from, to_date: to });
    return apiRequest<LeaveCalendarEntry[]>(`/leave-calendar?${query}`);
  },
  listLeaveRequests(filters: { userId?: string; status?: LeaveStatus; page?: number } = {}) {
    const query = new URLSearchParams({ page: String(filters.page ?? 0) });
    if (filters.userId) query.set("user_id", filters.userId);
    if (filters.status) query.set("status", filters.status);
    return apiRequest<LeaveListing>(`/leave-requests?${query}`);
  },
  listInboxLeaveRequests(page = 0) { return apiRequest<LeaveListing>(`/inbox/leave-requests?page=${page}`); },
  getLeaveRequest(id: string) { return apiRequest<LeaveRequest>(`/leave-requests/${id}`); },
  decideLeaveRequest(id: string, status: "APPROVED" | "REJECTED", comment: string) {
    return apiRequest<LeaveRequest>(`/leave-requests/${id}/decision`, { method: "POST", body: { status, comment } });
  },
  setStaffReportingManager(organizationUserId: string, managerOrganizationUserId: string) {
    return apiRequest<StaffReportingView>(`/staff-reporting/users/${organizationUserId}/reports-to`, {
      method: "PATCH",
      body: { manager_organization_user_id: managerOrganizationUserId }
    });
  },
  clearStaffReportingManager(organizationUserId: string) {
    return apiRequest<StaffReportingView>(`/staff-reporting/users/${organizationUserId}/reports-to`, { method: "DELETE" });
  },
  listHierarchyLevels() {
    return apiRequest<OrgHierarchyLevel[]>("/organization-hierarchy/levels");
  },
  createHierarchyLevel(body: { display_name: string; level_code?: string | null; level_order?: number | null }) {
    return apiRequest<OrgHierarchyLevel>("/organization-hierarchy/levels", {
      method: "POST",
      body
    });
  },
  updateHierarchyLevel(levelId: string, body: { display_name?: string | null; level_order?: number | null }) {
    return apiRequest<OrgHierarchyLevel>(`/organization-hierarchy/levels/${levelId}`, {
      method: "PATCH",
      body
    });
  },
  deactivateHierarchyLevel(levelId: string) {
    return apiRequest<OrgHierarchyLevel>(`/organization-hierarchy/levels/${levelId}/deactivate`, {
      method: "POST"
    });
  },
  reactivateHierarchyLevel(levelId: string) {
    return apiRequest<OrgHierarchyLevel>(`/organization-hierarchy/levels/${levelId}/reactivate`, {
      method: "POST"
    });
  },
  listHierarchyUsers() {
    return apiRequest<OrganizationHierarchyUser[]>("/organization-hierarchy/users");
  },
  setUserHierarchyLevel(organizationUserId: string, levelId: string | null) {
    return apiRequest<OrganizationHierarchyUser>(`/organization-hierarchy/users/${organizationUserId}/hierarchy`, {
      method: "PATCH",
      body: { level_id: levelId }
    });
  },
  setReportsTo(organizationUserId: string, managerOrganizationUserId: string | null) {
    return apiRequest<OrganizationReportingUpdateResponse>(`/organization-hierarchy/users/${organizationUserId}/reports-to`, {
      method: "PATCH",
      body: { manager_organization_user_id: managerOrganizationUserId }
    });
  },
  clearReportsTo(organizationUserId: string) {
    return apiRequest<OrganizationReportingUpdateResponse>(`/organization-hierarchy/users/${organizationUserId}/reports-to`, {
      method: "DELETE"
    });
  },
  listEffectiveLocations(organizationUserId: string) {
    return apiRequest<OrgLocationAccess[]>(`/organization-hierarchy/users/${organizationUserId}/effective-locations`);
  },
  listDirectUsersForLocation(locationId: string) {
    return apiRequest<OrgLocationDirectUser[]>(`/organization-hierarchy/locations/${locationId}/direct-users`);
  },
  addDirectLocationUser(locationId: string, organizationUserId: string) {
    return apiRequest<OrgLocationDirectUser>(`/organization-hierarchy/locations/${locationId}/direct-users`, {
      method: "POST",
      body: { organization_user_id: organizationUserId }
    });
  },
  removeDirectLocationUser(locationId: string, organizationUserId: string) {
    return apiRequest<{ removed: boolean }>(`/organization-hierarchy/locations/${locationId}/direct-users/${organizationUserId}`, {
      method: "DELETE"
    });
  },
  listVendors(search?: string) {
    const params = new URLSearchParams();
    if (search) {
      params.set("search", search);
    }
    return apiRequest<VendorsResponse>(`/vendors${params.size ? `?${params}` : ""}`);
  },
  listOrganizationConnections() {
    return apiRequest<OrganizationConnectionsResponse>("/org-connections");
  },
  listProjectConnectionOptions() {
    return apiRequest<OrganizationConnectionsResponse>("/org-connections/project-options");
  },
  discoverConnectionOrganizations(search?: string) {
    const params = new URLSearchParams();
    if (search) {
      params.set("search", search);
    }
    return apiRequest<OrganizationConnectionDiscoveryResponse>(`/org-connections/discover${params.size ? `?${params}` : ""}`);
  },
  listReceivedConnectionRequests(status = "PENDING") {
    const params = new URLSearchParams();
    if (status) {
      params.set("status", status);
    }
    return apiRequest<OrganizationConnectionRequestsResponse>(`/org-connections/requests/received${params.size ? `?${params}` : ""}`);
  },
  listSentConnectionRequests(status?: string) {
    const params = new URLSearchParams();
    if (status) {
      params.set("status", status);
    }
    return apiRequest<OrganizationConnectionRequestsResponse>(`/org-connections/requests/sent${params.size ? `?${params}` : ""}`);
  },
  sendConnectionRequest(targetOrganizationId: string) {
    return apiRequest<OrganizationConnectionRequest>("/org-connections/requests", {
      method: "POST",
      body: { target_organization_id: targetOrganizationId }
    });
  },
  acceptConnectionRequest(requestId: string) {
    return apiRequest<OrganizationConnection>(`/org-connections/requests/${requestId}/accept`, {
      method: "POST"
    });
  },
  rejectConnectionRequest(requestId: string) {
    return apiRequest<OrganizationConnectionRequest>(`/org-connections/requests/${requestId}/reject`, {
      method: "POST"
    });
  },
  cancelConnectionRequest(requestId: string) {
    return apiRequest<OrganizationConnectionRequest>(`/org-connections/requests/${requestId}/cancel`, {
      method: "POST"
    });
  },
  disconnectOrganization(organizationId: string) {
    return apiRequest<OrganizationConnection>(`/org-connections/${organizationId}/disconnect`, {
      method: "POST"
    });
  },
  blockOrganization(organizationId: string) {
    return apiRequest<OrganizationConnectionBlock>(`/org-connections/${organizationId}/block`, {
      method: "POST"
    });
  },
  unblockOrganization(organizationId: string) {
    return apiRequest<OrganizationConnectionBlock>(`/org-connections/${organizationId}/unblock`, {
      method: "POST"
    });
  },
  listBlockedOrganizations() {
    return apiRequest<OrganizationConnectionBlocksResponse>("/org-connections/blocks");
  },
  listRateCardCounterparties(search?: string, relationship?: "BUYING" | "SELLING") {
    const params = new URLSearchParams();
    if (search) {
      params.set("search", search);
    }
    if (relationship) {
      params.set("relationship", relationship);
    }
    return apiRequest<RateCardCounterpartiesResponse>(`/rate-cards/counterparties${params.size ? `?${params}` : ""}`);
  },
  listLocations(params: { search?: string; groupId?: string } = {}) {
    const query = new URLSearchParams();
    if (params.search) {
      query.set("search", params.search);
    }
    if (params.groupId) {
      query.set("group_id", params.groupId);
    }
    return apiRequest<LocationsResponse>(`/locations${query.size ? `?${query}` : ""}`);
  },
  listLocationGroups() {
    return apiRequest<LocationGroupsResponse>("/locations/groups");
  },
  createLocationGroup(groupName: string, locationIds: string[] = []) {
    const body = locationIds.length
      ? { group_name: groupName, location_ids: locationIds }
      : { group_name: groupName };
    return apiRequest<LocationGroupResponse>("/locations/groups", {
      method: "POST",
      body
    });
  },
  listLocationUploads() {
    return apiRequest<MasterDataUploadsResponse>("/locations/uploads");
  },
  createLocationUpload(fileUploadId: string) {
    return apiRequest<MasterDataUploadResponse>("/locations/uploads", {
      method: "POST",
      body: { file_upload_id: fileUploadId }
    });
  },
  listCatalogueItems(search?: string) {
    const query = new URLSearchParams();
    if (search) {
      query.set("search", search);
    }
    return apiRequest<CatalogueItemsResponse>(`/catalogue/items${query.size ? `?${query}` : ""}`);
  },
  listAssets(params: { search?: string; status?: string; locationId?: string; itemId?: string; tagId?: string } = {}) {
    const query = new URLSearchParams();
    if (params.search) query.set("search", params.search);
    if (params.status) query.set("status", params.status);
    if (params.locationId) query.set("location_id", params.locationId);
    if (params.itemId) query.set("item_id", params.itemId);
    if (params.tagId) query.set("tag_id", params.tagId);
    return apiRequest<AssetsResponse>(`/assets${query.size ? `?${query}` : ""}`);
  },
  getAsset(assetId: string) {
    return apiRequest<LocationAsset>(`/assets/${assetId}`);
  },
  createAsset(body: Record<string, unknown>, idempotencyKey: string) {
    return apiRequest<LocationAsset>("/assets", {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body
    });
  },
  updateAsset(assetId: string, body: Record<string, unknown>) {
    return apiRequest<LocationAsset>(`/assets/${assetId}`, { method: "PATCH", body });
  },
  deleteAsset(assetId: string) {
    return apiRequest<void>(`/assets/${assetId}`, { method: "DELETE" });
  },
  addAssetMedia(assetId: string, fileUploadIds: string[]) {
    return apiRequest<LocationAsset>(`/assets/${assetId}/media`, {
      method: "POST",
      body: { file_upload_ids: fileUploadIds }
    });
  },
  removeAssetMedia(assetId: string, mediaId: string) {
    return apiRequest<void>(`/assets/${assetId}/media/${mediaId}`, { method: "DELETE" });
  },
  listAssetTags() {
    return apiRequest<AssetTagsResponse>("/asset-tags");
  },
  createAssetTag(name: string, description?: string) {
    return apiRequest<AssetTag>("/asset-tags", { method: "POST", body: { name, description } });
  },
  convertProjectItemToAsset(
    projectId: string,
    locationId: string,
    itemLineId: string,
    body: Record<string, unknown>,
    idempotencyKey: string
  ) {
    return apiRequest<LocationAsset>(`/projects/${projectId}/locations/${locationId}/item-lines/${itemLineId}/asset`, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body
    });
  },
  listCreateProjectRequests(status = "unfinished") {
    const query = new URLSearchParams();
    if (status) {
      query.set("status", status);
    }
    return apiRequest<ListCreateProjectRequestsResponse>(`/create-project-requests${query.size ? `?${query}` : ""}`);
  },
  createProjectRequest(body: {
    customer_id?: string | null;
    project_name: string;
    budget?: number | null;
    vendor_id?: string | null;
    brand_owner_id?: string | null;
    project_owner_id?: string | null;
    po_number?: string | null;
    recce_required: boolean;
    source_type?: ProjectCreationSourceType;
    file_upload_id: string;
    supersedes_create_project_request_id?: string | null;
  }) {
    return apiRequest<CreateProjectRequestDetail>("/create-project-requests", {
      method: "POST",
      body: {
        project_name: body.project_name,
        customer_id: body.customer_id ?? null,
        budget: body.budget ?? null,
        vendor_id: body.vendor_id ?? null,
        brand_owner_id: body.brand_owner_id ?? null,
        project_owner_id: body.project_owner_id ?? null,
        po_number: body.po_number ?? null,
        recce_required: body.recce_required,
        source_type: body.source_type ?? "PROJECT_FILE",
        file_upload_id: body.file_upload_id,
        supersedes_create_project_request_id: body.supersedes_create_project_request_id ?? null
      }
    });
  },
  getStoreManagerProvisioningTask(taskId: string) {
    return apiRequest<StoreManagerProvisioningTask>(`/store-manager-provisioning/tasks/${taskId}`);
  },
  decideStoreManagerProvisioningTask(
    taskId: string,
    action: "approve" | "reject",
    rows: { row_id: string; reports_to_membership_id?: string | null }[]
  ) {
    return apiRequest<StoreManagerProvisioningTask>(`/store-manager-provisioning/tasks/${taskId}/${action}`, {
      method: "POST",
      body: { rows }
    });
  },
  listComplianceLocations(projectId: string) {
    return apiRequest<ComplianceLocationOption[]>(`/store-compliance/projects/${projectId}/locations`);
  },
  createComplianceTasks(projectId: string, tasks: { project_location_id: string; project_item_ids?: string[]; reason?: string | null }[]) {
    return apiRequest<StoreComplianceTask[]>(`/store-compliance/projects/${projectId}/tasks`, {
      method: "POST",
      body: { tasks }
    });
  },
  getStoreComplianceTask(taskId: string) {
    return apiRequest<StoreComplianceTask>(`/store-compliance/tasks/${taskId}`);
  },
  attachStoreComplianceInvoices(
    taskId: string,
    fileUploadIds: string[],
    invoiceNumber: string,
    invoiceDate: string,
    source: "TASK" | "LOCATION_DETAIL" | "BULK" = "TASK"
  ) {
    return apiRequest<StoreComplianceTask>(`/store-compliance/tasks/${taskId}/invoices`, {
      method: "POST",
      body: { file_upload_ids: fileUploadIds, invoice_number: invoiceNumber, invoice_date: invoiceDate, source }
    });
  },
  previewBulkStoreInvoices(projectId: string, fileNames: string[]) {
    return apiRequest<BulkInvoicePreview>(`/store-compliance/projects/${projectId}/invoices/preview`, {
      method: "POST",
      body: { file_names: fileNames }
    });
  },
  attachBulkStoreInvoices(projectId: string, files: { file_upload_id: string; task_id: string }[]) {
    return apiRequest<BulkInvoiceCommitResult>(`/store-compliance/projects/${projectId}/invoices/bulk`, {
      method: "POST",
      body: { files }
    });
  },
  deleteStoreComplianceInvoice(taskId: string, invoiceId: string) {
    return apiRequest<StoreComplianceTask>(`/store-compliance/tasks/${taskId}/invoices/${invoiceId}`, { method: "DELETE" });
  },
  listStoreComplianceComments(taskId: string) {
    return apiRequest<TaskComment[]>(`/store-compliance/tasks/${taskId}/comments`);
  },
  addStoreComplianceComment(taskId: string, messageText: string) {
    return apiRequest<TaskComment>(`/store-compliance/tasks/${taskId}/comments`, {
      method: "POST",
      body: { message_text: messageText }
    });
  },
  getStoreComplianceSettings() {
    return apiRequest<StoreComplianceSettings>("/store-compliance/settings");
  },
  updateStoreComplianceSettings(body: { grn_enabled: boolean; grn_step_label: string; invoice_required_for_grn: boolean }) {
    return apiRequest<StoreComplianceSettings>("/store-compliance/settings", { method: "PUT", body });
  },
  listOrganizationSettings() {
    return apiRequest<OrganizationSettingsResponse>("/organization-settings/groups");
  },
  listBlockerReasons(includeInactive = false, taskType?: string) {
    const params = new URLSearchParams({ include_inactive: String(includeInactive) });
    if (taskType) params.set("task_type", taskType);
    return apiRequest<BlockerReasonsResponse>(`/task-blockers/reasons?${params}`);
  },
  createBlockerReason(body: { task_type: string; name: string; description?: string | null }) {
    return apiRequest<BlockerReasonsResponse>("/task-blockers/reasons", {
      method: "POST",
      body
    });
  },
  updateBlockerReason(reasonId: string, body: { name: string; description?: string | null; active: boolean }) {
    return apiRequest<BlockerReasonsResponse>(`/task-blockers/reasons/${encodeURIComponent(reasonId)}`, { method: "PUT", body });
  },
  deactivateBlockerReason(reasonId: string) {
    return apiRequest<BlockerReasonsResponse>(`/task-blockers/reasons/${encodeURIComponent(reasonId)}`, {
      method: "DELETE"
    });
  },
  listOnDutyRoleSettings() {
    return apiRequest<OrganizationOnDutyRoleSettingsResponse>("/organization-settings/on-duty-role-settings");
  },
  updateOnDutyRoleSetting(roleCode: OrganizationRoleCode, body: {
    on_duty_feature_enabled: boolean;
    on_duty_location_tracking_enabled: boolean;
  }) {
    return apiRequest<OrganizationOnDutyRoleSettingsResponse>(
      `/organization-settings/on-duty-role-settings/${encodeURIComponent(roleCode)}`,
      { method: "PUT", body }
    );
  },
  getOrganizationCommercialProfile() {
    return apiRequest<OrganizationCommercialProfile>("/organization-commercial-profile");
  },
  getOrganizationProjectPoc(organizationId: string) {
    return apiRequest<import("./types").OrganizationProjectPocView>(`/organizations/${encodeURIComponent(organizationId)}/project-poc`);
  },
  updateOrganizationProjectPoc(organizationId: string, body: { user_id: string; expected_updated_at: string }) {
    return apiRequest<import("./types").OrganizationProjectPocView>(`/organizations/${encodeURIComponent(organizationId)}/project-poc`, { method: "PUT", body });
  },
  updateOrganizationCommercialProfile(body: {
    legal_name: string;
    gstin?: string | null;
    phone?: string | null;
    email?: string | null;
  }) {
    return apiRequest<OrganizationCommercialProfile>("/organization-commercial-profile", { method: "PUT", body });
  },
  getProductionUnit(scope: "project", id: string) {
    return apiRequest<import("./types").ProductionUnitSelection>(`/vendor/projects/${id}/production-unit`);
  },
  setProductionUnit(scope: "project", id: string, addressId: string | null) {
    return apiRequest<import("./types").ProductionUnitSelection>(`/vendor/projects/${id}/production-unit`, {
      method: "PUT", body: { address_id: addressId }
    });
  },
  createOrganizationAddress(body: {
    kind: OrganizationAddressKind;
    latitude: number;
    longitude: number;
    place_id: string | null;
    label: string;
    address_line1: string;
    address_line2?: string | null;
    city: string;
    state_name: string;
    state_code?: string | null;
    postal_code: string;
    country_code: string;
    contact_name?: string | null;
    contact_phone?: string | null;
    default_address: boolean;
  }, shippingSameAsBilling = false) {
    return apiRequest<OrganizationCommercialProfile>(`/organization-commercial-profile/addresses${shippingSameAsBilling ? "?shippingSameAsBilling=true" : ""}`, { method: "POST", body });
  },
  updateOrganizationAddress(addressId: string, body: {
    kind: OrganizationAddressKind;
    latitude: number;
    longitude: number;
    place_id: string | null;
    label: string;
    address_line1: string;
    address_line2?: string | null;
    city: string;
    state_name: string;
    state_code?: string | null;
    postal_code: string;
    country_code: string;
    contact_name?: string | null;
    contact_phone?: string | null;
    default_address: boolean;
  }) {
    return apiRequest<OrganizationCommercialProfile>(`/organization-commercial-profile/addresses/${addressId}`, { method: "PUT", body });
  },
  deleteOrganizationAddress(addressId: string) {
    return apiRequest<OrganizationCommercialProfile>(`/organization-commercial-profile/addresses/${addressId}`, { method: "DELETE" });
  },
  updateOrganizationSetting(
    settingKey: string,
    body: {
      boolean_value?: boolean | null;
      string_value?: string | null;
      number_value?: number | null;
      json_value?: string | null;
    }
  ) {
    return apiRequest<OrganizationSettingsResponse>(`/organization-settings/settings/${settingKey}`, {
      method: "PUT",
      body
    });
  },
  listOrganizationTabPermissions() {
    return apiRequest<OrganizationTabPermissionMatrix>("/organization-settings/tab-permissions");
  },
  updateOrganizationTabPermission(
    subjectType: string,
    subjectKey: string,
    tabCode: string,
    body: { can_view: boolean; can_update: boolean }
  ) {
    return apiRequest<OrganizationTabPermissionMatrix>(
      `/organization-settings/tab-permissions/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectKey)}/${encodeURIComponent(tabCode)}`,
      { method: "PUT", body }
    );
  },
  markProjectFileUploadComplete(createProjectRequestId: string) {
    return apiRequest<CreateProjectRequestDetailResponse>(`/create-project-requests/${createProjectRequestId}/file-upload-complete`, {
      method: "POST"
    });
  },
  getCreateProjectRequest(createProjectRequestId: string) {
    return apiRequest<CreateProjectRequestDetailResponse>(`/create-project-requests/${createProjectRequestId}`);
  },
  getProjectValidationReport(createProjectRequestId: string) {
    return apiRequest<DownloadTargetResponse>(`/create-project-requests/${createProjectRequestId}/validation-report`);
  },
  getAgencyProjectSampleTemplate() {
    return apiRequest<DownloadTargetResponse>("/project-file-templates/agency-project-sample");
  },
  createCatalogueProjectDraft(projectName: string, budget?: number | null, customerId?: string | null) {
    return apiRequest<CatalogueProject>("/catalogue-projects", {
      method: "POST",
      body: { project_name: projectName, customer_id: customerId, budget: budget ?? null }
    });
  },
  getCatalogueProjectDraft(projectId: string) {
    return apiRequest<CatalogueProject>(`/catalogue-projects/${projectId}`);
  },
  getProjectCollaboration(projectId: string) {
    return apiRequest<CatalogueProject>(`/projects/${projectId}/collaboration`);
  },
  renameCatalogueProjectDraft(projectId: string, projectName: string, budget?: number | null) {
    return apiRequest<CatalogueProject>(`/catalogue-projects/${projectId}`, {
      method: "PATCH",
      body: { project_name: projectName, budget: budget ?? null }
    });
  },
  saveCatalogueProjectScope(projectId: string, items: {
    catalogue_item_id: string;
    location_group_id?: string | null;
    apply_to_all_locations: boolean;
    apply_to_all_in_group: boolean;
    location_ids: string[];
    default_quantity: number;
    recce_required: boolean;
    width?: number | null;
    height?: number | null;
    artwork_file_name?: string | null;
  }[], completeStep = false) {
    return apiRequest<CatalogueProject>(`/catalogue-projects/${projectId}/scope`, {
      method: "PUT",
      body: { items, complete_step: completeStep }
    });
  },
  initProjectArtworkUploads(projectId: string, files: {
    file_name: string;
    content_type: string;
    size_bytes: number;
  }[]) {
    return apiRequest<ProjectArtworkUploadInitResponse>(`/projects/${projectId}/artworks/upload-init`, {
      method: "POST",
      body: { files }
    });
  },
  completeProjectArtworkUpload(projectId: string, projectArtworkId: string) {
    return apiRequest<ProjectArtworkUploadCompleteResponse>(`/projects/${projectId}/artworks/${projectArtworkId}/upload-complete`, {
      method: "POST"
    });
  },
  listDesignerProjectTaskDetails(projectId: string) {
    return apiRequest<FieldOpsTaskDetails[]>(`/field-ops/designer/projects/${projectId}/tasks`);
  },
  listDesignerProjectArtworks(projectId: string) {
    return apiRequest<ListProjectArtworksResponse>(`/designer/projects/${projectId}/artworks`);
  },
  initDesignerProjectArtworkUploads(projectId: string, files: {
    file_name: string;
    content_type: string;
    size_bytes: number;
  }[]) {
    return apiRequest<ProjectArtworkUploadInitResponse>(`/designer/projects/${projectId}/artworks/upload-init`, {
      method: "POST",
      body: { files }
    });
  },
  getDesignerProjectArtworkUploadParts(projectId: string, projectArtworkId: string, partNumbers: number[]) {
    return apiRequest<ProjectArtworkUploadPartsResponse>(`/designer/projects/${projectId}/artworks/${projectArtworkId}/multipart-parts`, {
      method: "POST",
      body: { part_numbers: partNumbers }
    });
  },
  completeDesignerProjectArtworkUpload(projectId: string, projectArtworkId: string) {
    return apiRequest<ProjectArtworkUploadCompleteResponse>(`/designer/projects/${projectId}/artworks/${projectArtworkId}/upload-complete`, {
      method: "POST"
    });
  },
  mapDesignerProjectArtwork(projectId: string, projectArtworkId: string, itemLineIds: string[]) {
    return apiRequest<UpdateProjectArtworkItemLinesResponse>(`/designer/projects/${projectId}/artworks/${projectArtworkId}/item-lines`, {
      method: "PUT",
      body: { item_line_ids: itemLineIds }
    });
  },
  listDesignerProjectArtworkMockups(projectId: string) {
    return apiRequest<ListProjectArtworkMockupsResponse>(`/designer/projects/${projectId}/artwork-mockups`);
  },
  initDesignerProjectArtworkMockupUploads(projectId: string, files: {
    file_name: string;
    content_type: string;
    size_bytes: number;
  }[]) {
    return apiRequest<ProjectArtworkMockupUploadInitResponse>(`/designer/projects/${projectId}/artwork-mockups/upload-init`, {
      method: "POST",
      body: { files }
    });
  },
  getDesignerProjectArtworkMockupUploadParts(projectId: string, projectArtworkMockupId: string, partNumbers: number[]) {
    return apiRequest<ProjectArtworkUploadPartsResponse>(`/designer/projects/${projectId}/artwork-mockups/${projectArtworkMockupId}/multipart-parts`, {
      method: "POST",
      body: { part_numbers: partNumbers }
    });
  },
  completeDesignerProjectArtworkMockupUpload(projectId: string, projectArtworkMockupId: string) {
    return apiRequest<ProjectArtworkMockupMutationResponse>(`/designer/projects/${projectId}/artwork-mockups/${projectArtworkMockupId}/upload-complete`, {
      method: "POST"
    });
  },
  mapDesignerProjectArtworkMockup(projectId: string, projectArtworkMockupId: string, itemLineIds: string[]) {
    return apiRequest<ProjectArtworkMockupMutationResponse>(`/designer/projects/${projectId}/artwork-mockups/${projectArtworkMockupId}/item-lines`, {
      method: "PUT",
      body: { item_line_ids: itemLineIds }
    });
  },
  listCatalogueProjectRows(projectId: string) {
    return apiRequest<CatalogueProjectRowsResponse>(`/catalogue-projects/${projectId}/rows`);
  },
  listCatalogueProjectPrinterEstimates(projectId: string) {
    return apiRequest<ProjectPrinterEstimatesResponse>(`/catalogue-projects/${projectId}/printer-estimates`);
  },
  saveCatalogueProjectRows(projectId: string, rows: {
    row_id: string;
    width?: number | null;
    height?: number | null;
    quantity: number;
    location_budget?: number | null;
    comment?: string | null;
  }[], completeStep = false) {
    return apiRequest<CatalogueProjectRowsResponse>(`/catalogue-projects/${projectId}/rows`, {
      method: "PUT",
      body: {
        rows,
        complete_step: completeStep
      }
    });
  },
  saveProjectCollaboration(projectId: string, body: ProjectCollaborationRequestBody, completeStep = false) {
    return apiRequest<CatalogueProject>(`/projects/${projectId}/collaboration`, {
      method: completeStep ? "POST" : "PUT",
      body
    });
  },
  saveCatalogueProjectCollaboration(projectId: string, body: ProjectCollaborationRequestBody, completeStep = false) {
    return apiRequest<CatalogueProject>(`/projects/${projectId}/collaboration`, {
      method: completeStep ? "POST" : "PUT",
      body
    });
  },
  startCatalogueProjectExecution(projectId: string) {
    return apiRequest<CatalogueProject>(`/catalogue-projects/${projectId}/start-execution`, { method: "POST" });
  },
  notifyCatalogueProjectPartner(projectId: string, role: "PRINTER" | "RECCE" | "DESIGNER") {
    return apiRequest<CatalogueProject>(`/catalogue-projects/${projectId}/partners/${role}/notify`, { method: "POST" });
  },
  listCatalogueProjectPartners(type: "PRINTER" | "RECCE" | "DESIGNER", search?: string) {
    const query = new URLSearchParams();
    query.set("type", type);
    if (search) {
      query.set("search", search);
    }
    return apiRequest<CataloguePartnersResponse>(`/catalogue-projects/partners?${query}`);
  },
  listCatalogueProjectInternalUsers() {
    return apiRequest<InternalUsersResponse>("/catalogue-projects/internal-users");
  },
  listCatalogueUploads() {
    return apiRequest<MasterDataUploadsResponse>("/catalogue/uploads");
  },
  createCatalogueUpload(fileUploadId: string) {
    return apiRequest<MasterDataUploadResponse>("/catalogue/uploads", {
      method: "POST",
      body: { file_upload_id: fileUploadId }
    });
  },
  listRateCards(params: { status?: string; search?: string; page?: number; pageSize?: number }) {
    const query = new URLSearchParams();
    if (params.status && params.status !== "ALL") {
      query.set("status", params.status);
    }
    if (params.search) {
      query.set("search", params.search);
    }
    if (params.page) {
      query.set("page", String(params.page));
    }
    if (params.pageSize) {
      query.set("page_size", String(params.pageSize));
    }
    return apiRequest<RateCardsResponse>(`/rate-cards${query.size ? `?${query}` : ""}`);
  },
  listProjects(params: {
    direction?: "CLIENT_WORK" | "MY_WORK";
    view?: "active";
    status?: ProjectStatus;
    risk?: "HIGH";
    vendorId?: string;
    clientId?: string;
    limit?: number;
    cursor?: string;
  } = {}) {
    const query = new URLSearchParams();
    if (params.direction) query.set("direction", params.direction);
    if (params.view) {
      query.set("view", params.view);
    }
    if (params.status) {
      query.set("status", params.status);
    }
    if (params.risk) {
      query.set("risk", params.risk);
    }
    if (params.vendorId) {
      query.set("vendor_id", params.vendorId);
    }
    if (params.clientId) {
      query.set("client_id", params.clientId);
    }
    if (params.limit) {
      query.set("limit", String(params.limit));
    }
    if (params.cursor) {
      query.set("cursor", params.cursor);
    }
    return apiRequest<ListProjectsResponse>(`/projects${query.size ? `?${query}` : ""}`);
  },
  getProjectsSnapshot() {
    return apiRequest<ProjectsSnapshotResponse>("/projects/snapshot");
  },
  getProjectOverview(projectId: string) {
    return apiRequest<ProjectOverviewResponse>(`/projects/${projectId}/overview`);
  },
  getProjectDetails(projectId: string) {
    return apiRequest<ProjectDetailsResponse>(`/projects/${projectId}`);
  },
  listProjectLocations(projectId: string, params: { search?: string; limit?: number; cursor?: string } = {}) {
    const query = new URLSearchParams();
    if (params.search) {
      query.set("search", params.search);
    }
    if (params.limit) {
      query.set("limit", String(params.limit));
    }
    if (params.cursor) {
      query.set("cursor", params.cursor);
    }
    return apiRequest<ListProjectLocationsResponse>(`/projects/${projectId}/locations${query.size ? `?${query}` : ""}`);
  },
  getProjectLocationDetails(projectId: string, locationId: string) {
    return apiRequest<ProjectLocationDetails>(`/projects/${projectId}/locations/${locationId}`);
  },
  listProjectTimeline(projectId: string) {
    return apiRequest<ProjectTimelineResponse>(`/projects/${projectId}/timeline`);
  },
  listProjectFileExports(projectId: string) {
    return apiRequest<ListProjectFileExportsResponse>(`/projects/${projectId}/downloads/file-exports`);
  },
  listProjectReports(projectId: string) {
    return apiRequest<ListProjectReportsResponse>(`/projects/${projectId}/downloads/reports`);
  },
  requestInstallationReport(projectId: string, body: { location_ids: string[] }) {
    return apiRequest<ProjectDownload>(`/projects/${projectId}/downloads/installation-report`, {
      method: "POST",
      body
    });
  },
  requestProductionItemListDownload(projectId: string) {
    return apiRequest<ProjectDownload>(`/projects/${projectId}/items/downloads/production-item-list`, {
      method: "POST"
    });
  },
  listProjectFileExportAvailability(projectId: string) {
    return apiRequest<ListProjectFileExportAvailabilityResponse>(`/projects/${projectId}/downloads/file-exports/availability`);
  },
  listProjectArtworks(projectId: string) {
    return apiRequest<ListProjectArtworksResponse>(`/projects/${projectId}/artworks`);
  },
  updateProjectArtworkItemLines(projectId: string, projectArtworkId: string, itemLineIds: string[]) {
    return apiRequest<UpdateProjectArtworkItemLinesResponse>(`/projects/${projectId}/artworks/${projectArtworkId}/item-lines`, {
      method: "PUT",
      body: { item_line_ids: itemLineIds }
    });
  },
  deleteProjectArtwork(projectId: string, projectArtworkId: string) {
    return apiRequest<ProjectArtworkUploadCompleteResponse>(`/projects/${projectId}/artworks/${projectArtworkId}`, {
      method: "DELETE"
    });
  },
  requestProjectFileExport(projectId: string, body: { export_type: ProjectFileExportType; location_ids: string[] }) {
    return apiRequest<ProjectFileExportResponse>(`/projects/${projectId}/downloads/file-exports`, {
      method: "POST",
      body
    });
  },
  getProjectFileExport(projectId: string, requestId: string) {
    return apiRequest<ProjectFileExportResponse>(`/projects/${projectId}/downloads/file-exports/${requestId}`);
  },
  listProjectTasks(projectId: string, params: { type?: string; status?: string; assignmentType?: string; includeCompleted?: boolean } = {}) {
    const query = new URLSearchParams();
    if (params.type) {
      query.set("type", params.type);
    }
    if (params.status) {
      query.set("status", params.status);
    }
    if (params.assignmentType) {
      query.set("assignment_type", params.assignmentType);
    }
    if (params.includeCompleted !== undefined) {
      query.set("include_completed", String(params.includeCompleted));
    }
    return apiRequest<ProjectTask[]>(`/vendor/projects/${projectId}/tasks${query.size ? `?${query}` : ""}`);
  },
  createGeneralTask(body: {
    title: string; description: string | null; project_id: string | null; location_id: string | null;
    assignment_type: "INDIVIDUAL" | "TEAM" | null; assignee_id: string | null; due_at: string | null;
  }) {
    return apiRequest<ProjectTask>("/workspace/tasks", { method: "POST", body });
  },
  getGeneralTaskLocations() {
    return apiRequest<{ location_id: string; location_code: string; location_name: string }[]>("/workspace/task-locations");
  },
  changeGeneralTaskStatus(taskId: string, action: "START" | "COMPLETE" | "REOPEN") {
    return apiRequest<ProjectTask>(`/workspace/tasks/${taskId}/status`, { method: "POST", body: { action } });
  },
  planGeneralTask(taskId: string, body: {
    assignment_type: "INDIVIDUAL" | "TEAM" | null; assignee_id: string | null;
    planned_start_at: string | null; planned_complete_at: string | null;
  }) {
    return apiRequest<ProjectTask>(`/workspace/tasks/${taskId}/plan`, { method: "PUT", body });
  },
  getProjectTask(taskId: string) {
    return apiRequest<ProjectTask>(`/vendor/task/${taskId}`);
  },
  blockProjectTask(taskId: string, body: { reason_id: string; comment?: string | null }) {
    return apiRequest<ProjectTask>(`/vendor/task/${taskId}/block`, {
      method: "POST",
      body
    });
  },
  unblockProjectTask(taskId: string, body: { comment?: string | null } = {}) {
    return apiRequest<ProjectTask>(`/vendor/task/${taskId}/unblock`, {
      method: "POST",
      body
    });
  },
  blockStoreTask(taskId: string, body: { reason_id: string; comment?: string | null }) {
    return apiRequest<ProjectTask>(`/store-compliance/tasks/${taskId}/block`, { method: "POST", body });
  },
  unblockStoreTask(taskId: string, body: { comment?: string | null } = {}) {
    return apiRequest<ProjectTask>(`/store-compliance/tasks/${taskId}/unblock`, { method: "POST", body });
  },
  getFieldOpsTaskDetails(taskId: string) {
    return apiRequest<FieldOpsTaskDetails>(`/field-ops/tasks/${taskId}`);
  },
  startFieldOpsTask(taskId: string) {
    return apiRequest<FieldOpsTaskDetails>(`/field-ops/tasks/${taskId}/start`, {
      method: "POST"
    });
  },
  recordFieldOpsTaskItemLinePhotos(taskId: string, itemLineId: string, fileUploadIds: string[]) {
    return apiRequest<FieldOpsTaskDetails>(
      `/field-ops/tasks/${taskId}/item-lines/${encodeURIComponent(itemLineId)}/photos`,
      {
        method: "POST",
        body: {
          photos: fileUploadIds.map((fileUploadId) => ({ file_upload_id: fileUploadId }))
        }
      }
    );
  },
  recordFieldOpsTaskItemLineEnrichedArtwork(taskId: string, itemLineId: string, fileUploadIds: string[]) {
    return apiRequest<FieldOpsTaskDetails>(
      `/field-ops/tasks/${taskId}/item-lines/${encodeURIComponent(itemLineId)}/enriched-artwork`,
      {
        method: "POST",
        body: { file_upload_ids: fileUploadIds }
      }
    );
  },
  recordFieldOpsTaskPhotos(taskId: string, fileUploadIds: string[]) {
    return apiRequest<FieldOpsTaskDetails>(`/field-ops/tasks/${taskId}/photos`, {
      method: "POST",
      body: {
        photos: fileUploadIds.map((fileUploadId) => ({ file_upload_id: fileUploadId }))
      }
    });
  },
  recordFieldOpsTaskProofOfDelivery(taskId: string, fileUploadIds: string[]) {
    return apiRequest<FieldOpsTaskDetails>(`/field-ops/tasks/${taskId}/proof-of-delivery`, {
      method: "POST",
      body: { file_upload_ids: fileUploadIds }
    });
  },
  addFieldOpsTaskComment(taskId: string, messageText: string) {
    return apiRequest<FieldOpsTaskDetails>(`/field-ops/tasks/${taskId}/comments`, {
      method: "POST",
      body: { message_text: messageText }
    });
  },
  completeFieldOpsTask(taskId: string) {
    return apiRequest<FieldOpsTaskDetails>(`/field-ops/tasks/${taskId}/complete`, {
      method: "POST"
    });
  },
  reopenFieldOpsTask(taskId: string) {
    return apiRequest<FieldOpsTaskDetails>(`/field-ops/tasks/${taskId}/reopen`, {
      method: "POST"
    });
  },
  createBulkProjectTasks(projectId: string, body: { type: string; location_ids: string[] }) {
    return apiRequest<ProjectTask[]>(`/vendor/projects/${projectId}/create-bulk-tasks`, {
      method: "POST",
      body
    });
  },
  sendRecceReport(projectId: string, body: { location_ids: string[] }) {
    return apiRequest<SendRecceReportResponse>(`/vendor/projects/${projectId}/recce-report`, {
      method: "POST",
      body
    });
  },
  sendInstallationFyi(projectId: string, body: { location_ids: string[] }) {
    return apiRequest<SendInstallationFyiResponse>(`/vendor/projects/${projectId}/installation-fyi`, {
      method: "POST",
      body
    });
  },
  listProductionBatchOptions(projectId: string) {
    return apiRequest<ProductionBatchItemLineOption[]>(`/vendor/projects/${projectId}/production-batch-options`);
  },
  createProductionBatch(projectId: string, body: { location_ids: string[]; item_line_ids?: string[] }) {
    return apiRequest<ProjectTask[]>(`/vendor/projects/${projectId}/production-batches`, {
      method: "POST",
      body
    });
  },
  assignProductionBatch(projectId: string, locationGroupId: string, body: {
    production_manager_id: string;
    delivery_manager_id: string;
    production_operator_id?: string | null;
    packaging_operator_id?: string | null;
  }) {
    return apiRequest<ProjectTask[]>(`/vendor/projects/${projectId}/production-batches/${locationGroupId}/assign`, {
      method: "POST",
      body
    });
  },
  assignProjectManagers(projectId: string, body: {
    project_manager_id?: string | null;
    production_manager_id?: string | null;
    delivery_manager_id?: string | null;
  }) {
    return apiRequest<ProjectSummary>(`/vendor/projects/${projectId}/managers`, {
      method: "PUT",
      body
    });
  },
  downloadProjectTerms: (id: string) => apiBlob(`/projects/${id}/terms/document`),
  getProjectTerms(projectId: string) { return apiRequest<import("../features/projects/projectTermsTypes").ProjectTerms>(`/projects/${projectId}/terms`); },
  getVendorProjectPocOptions(projectId: string) { return apiRequest<import("../features/projects/projectPoc").PocOptions>(`/projects/${projectId}/vendor-poc-options`); },
  confirmVendorProjectPoc(projectId: string, version: number, poc: import("../features/projects/projectPoc").ProjectPoc) {
    return apiRequest<import("../features/projects/projectTermsTypes").ProjectTerms>(`/projects/${projectId}/vendor-poc`, { method: "POST", body: { rate_revision_version: version, poc } });
  },
  updateProjectTerms(projectId: string, body: import("../features/projects/projectTermsTypes").ProjectTermsUpdate) {
    return apiRequest<import("../features/projects/projectTermsTypes").ProjectTerms>(`/projects/${projectId}/terms`, { method: "PUT", body });
  },
  projectTermsAction(projectId: string, action: "send" | "accept" | "reject" | "rates/accept" | "discard", version: number) {
    return apiRequest<import("../features/projects/projectTermsTypes").ProjectTerms>(`/projects/${projectId}/${action}`, { method: "POST", body: { rate_revision_version: version } });
  },
  proposeProjectRates(projectId: string, version: number, items: import("../features/projects/projectTermsTypes").ProjectRate[]) {
    return apiRequest<import("../features/projects/projectTermsTypes").ProjectTerms>(`/projects/${projectId}/rates/propose`, { method: "POST", body: { rate_revision_version: version, items } });
  },
  createVendorProject(projectId: string, body: { task_ids: string[] }) {
    return apiRequest<{ project_id: string }>(`/projects/${projectId}/vendor-projects`, { method: "POST", body });
  },
  getProjectMessages(projectId: string) { return apiRequest<import("../features/projects/projectTermsTypes").ProjectMessage[]>(`/projects/${projectId}/discussion/messages`); },
  addProjectMessage(projectId: string, body: { message_text: string; file_upload_ids: string[] }) {
    return apiRequest<import("../features/projects/projectTermsTypes").ProjectMessage[]>(`/projects/${projectId}/discussion/messages`, { method: "POST", body });
  },
  startProductionTask(taskId: string) {
    return apiRequest<ProjectTask>(`/vendor/task/${taskId}/start-production-task`, { method: "POST" });
  },
  startProductionBatch(taskId: string) {
    return apiRequest<ProjectTask>(`/vendor/task/${taskId}/start-production-batch`, {
      method: "POST"
    });
  },
  completeProductionBatch(taskId: string) {
    return apiRequest<ProjectTask>(`/vendor/task/${taskId}/complete-production-batch`, {
      method: "POST"
    });
  },
  assignProjectTask(taskId: string, body: {
    assignment_type: "INDIVIDUAL" | "TEAM";
    assignee_id: string;
  }) {
    return apiRequest<ProjectTask>(`/vendor/task/${taskId}/assign`, {
      method: "POST",
      body
    });
  },
  planProjectTask(taskId: string, body: {
    assignment_type: "INDIVIDUAL" | "TEAM" | null;
    assignee_id: string | null;
    planned_start_at: string | null;
    planned_complete_at: string | null;
  }) {
    return apiRequest<ProjectTask>(`/vendor/task/${taskId}/plan`, { method: "PUT", body });
  },
  completeProjectTask(taskId: string) {
    return apiRequest<ProjectTask>(`/vendor/task/${taskId}/complete`, {
      method: "POST"
    });
  },
  reopenProjectTask(taskId: string) {
    return apiRequest<ProjectTask>(`/vendor/task/${taskId}/reopen`, {
      method: "POST"
    });
  },
  listTaskHistory(taskId: string) {
    return apiRequest<TaskHistoryEvent[]>(`/vendor/task/${taskId}/history`);
  },
  listTaskComments(taskId: string) {
    return apiRequest<TaskComment[]>(`/vendor/task/${taskId}/comments`);
  },
  addTaskComment(taskId: string, body: { message_text: string }) {
    return apiRequest<TaskComment>(`/vendor/task/${taskId}/comments`, {
      method: "POST",
      body
    });
  },
  completeProject(projectId: string, allowIncompleteExecution: boolean) {
    return apiRequest<unknown>(`/vendor/projects/${projectId}/complete`, {
      method: "POST", body: { allow_incomplete_execution: allowIncompleteExecution }
    });
  },
  recordProjectStageChange(projectId: string, body: {
    timestamp_to_update: string;
    close_open_prior_tasks?: boolean;
    location_ids?: string[];
  }) {
    return apiRequest<unknown>(`/vendor/projects/${projectId}/record-stage-change`, {
      method: "POST",
      body
    });
  },
  bulkAssignProjectTasks(body: {
    task_ids: string[];
    assignment_type: "INDIVIDUAL" | "TEAM";
    assignee_id: string;
  }) {
    return apiRequest<ProjectTask[]>("/vendor/task/bulk-assign", {
      method: "POST",
      body: {
        ...body,
        user_id: body.assignment_type === "INDIVIDUAL" ? body.assignee_id : null,
        team_id: body.assignment_type === "TEAM" ? body.assignee_id : null
      }
    });
  },
  getComplianceLeadershipSummary() {
    return apiRequest<{ total_count: number; pending_count: number }>("/store-compliance/leadership/summary");
  },
  listWorkspaceTasks(params: WorkspaceTaskFilters = {}) {
    const query = new URLSearchParams();
    const fields = { scope: params.scope, project_id: params.projectId, location_id: params.locationId,
      user_id: params.userId, team_id: params.teamId, assignment_type: params.assignmentType,
      type: params.type, status: params.status, search: params.search, include_completed: params.includeCompleted,
      limit: params.limit, cursor: params.cursor };
    for (const [key, value] of Object.entries(fields)) if (value !== undefined && value !== "") query.set(key, String(value));
    return apiRequest<WorkspaceTaskPage>(`/workspace/tasks?${query}`);
  },
  listAllWorkspaceTasks(params: Omit<WorkspaceTaskFilters, "cursor" | "limit"> = {}) {
    return collectTaskPages(cursor => api.listWorkspaceTasks({ ...params, limit: 100, cursor }));
  },
  listWorkspaceInbox(includeCompleted = false) {
    return apiRequest<ProjectTask[]>(`/workspace/inbox?include_completed=${includeCompleted}`);
  },
  getWorkspaceTask(taskId: string) {
    return apiRequest<ProjectTask>(`/workspace/tasks/${encodeURIComponent(taskId)}`);
  },
  listInboxTasks(params: {
    type?: string;
    status?: string;
    assignmentType?: string;
    search?: string;
    includeCompleted?: boolean;
  } = {}) {
    const query = new URLSearchParams();
    if (params.type) query.set("type", params.type);
    if (params.status) query.set("status", params.status);
    if (params.assignmentType) query.set("assignment_type", params.assignmentType);
    if (params.search) query.set("search", params.search);
    if (params.includeCompleted !== undefined) query.set("include_completed", String(params.includeCompleted));
    return apiRequest<ProjectTask[]>(`/inbox/tasks${query.size ? `?${query}` : ""}`);
  },
  listAgencyInboxTasks(params: {
    type?: string;
    status?: string;
    assignmentType?: string;
    search?: string;
    includeCompleted?: boolean;
  } = {}) {
    const query = new URLSearchParams();
    if (params.type) query.set("type", params.type);
    if (params.status) query.set("status", params.status);
    if (params.assignmentType) query.set("assignment_type", params.assignmentType);
    if (params.search) query.set("search", params.search);
    if (params.includeCompleted !== undefined) query.set("include_completed", String(params.includeCompleted));
    return apiRequest<ProjectTask[]>(`/agency/tasks${query.size ? `?${query}` : ""}`);
  },
  completeInboxTask(taskId: string) {
    return apiRequest<ProjectTask>(`/inbox/task/${taskId}/complete`, {
      method: "POST"
    });
  },
  reopenInboxTask(taskId: string) {
    return apiRequest<ProjectTask>(`/inbox/task/${taskId}/reopen`, {
      method: "POST"
    });
  },
  completeAgencyTask(taskId: string) {
    return apiRequest<ProjectTask>(`/agency/task/${taskId}/complete`, {
      method: "POST"
    });
  },
  reopenAgencyTask(taskId: string) {
    return apiRequest<ProjectTask>(`/agency/task/${taskId}/reopen`, {
      method: "POST"
    });
  },
  listInboxTaskHistory(taskId: string) {
    return apiRequest<TaskHistoryEvent[]>(`/inbox/task/${taskId}/history`);
  },
  listAgencyTaskHistory(taskId: string) {
    return apiRequest<TaskHistoryEvent[]>(`/agency/task/${taskId}/history`);
  },
  listInboxTaskComments(taskId: string) {
    return apiRequest<TaskComment[]>(`/inbox/task/${taskId}/comments`);
  },
  listAgencyTaskComments(taskId: string) {
    return apiRequest<TaskComment[]>(`/agency/task/${taskId}/comments`);
  },
  addInboxTaskComment(taskId: string, body: { message_text: string }) {
    return apiRequest<TaskComment>(`/inbox/task/${taskId}/comments`, {
      method: "POST",
      body
    });
  },
  addAgencyTaskComment(taskId: string, body: { message_text: string }) {
    return apiRequest<TaskComment>(`/agency/task/${taskId}/comments`, {
      method: "POST",
      body
    });
  },
  getVendorResources() {
    return apiRequest<VendorResourceOptions>("/vendor/resources");
  },
  listEligibleRouteTasks(params: { taskType?: string; projectId?: string; search?: string } = {}) {
    const query = new URLSearchParams();
    query.set("task_type", params.taskType ?? "RECCE");
    if (params.projectId) query.set("project_id", params.projectId);
    if (params.search) query.set("search", params.search);
    return apiRequest<RoutePlanningTask[]>(`/vendor/field-route-plans/eligible-tasks?${query}`);
  },
  getGoogleMapsConfiguration() {
    return apiRequest<GoogleMapsConfiguration>("/vendor/google-maps/configuration");
  },
  createFieldRoutePlan(body: {
    task_type: "RECCE" | "INSTALLATION";
    task_ids: string[];
    workers: {
      assignment_type: "INDIVIDUAL" | "TEAM";
      assignee_id: string;
      start: { name?: string | null; latitude: number; longitude: number };
    }[];
  }) {
    return apiRequest<FieldRoutePlan>("/vendor/field-route-plans", { method: "POST", body });
  },
  getFieldRoutePlan(planId: string) {
    return apiRequest<FieldRoutePlan>(`/vendor/field-route-plans/${planId}`);
  },
  moveFieldRouteTask(planId: string, body: {
    base_version_id: string;
    task_id: string;
    assignment_type: "INDIVIDUAL" | "TEAM";
    assignee_id: string;
  }) {
    return apiRequest<FieldRoutePlan>(`/vendor/field-route-plans/${planId}/route-adjustments`, {
      method: "POST",
      body
    });
  },
  reoptimizeFieldRoutePlan(planId: string, baseVersionId: string) {
    return apiRequest<FieldRoutePlan>(`/vendor/field-route-plans/${planId}/reoptimize`, {
      method: "POST",
      body: { base_version_id: baseVersionId }
    });
  },
  assignFieldRoutePlan(planId: string) {
    return apiRequest<FieldRoutePlan>(`/vendor/field-route-plans/${planId}/assign`, { method: "POST" });
  },
  createRateCard(body: {
    vendor_id?: string | null;
    agency_id?: string | null;
    name: string;
    valid_from: string;
    valid_until?: string | null;
    currency_code: string;
    creation_method: string;
    import_file_upload_id?: string | null;
  }) {
    return apiRequest<RateCardDetailResponse>("/rate-cards", { method: "POST", body });
  },
  getRateCard(rateCardId: string) {
    return apiRequest<RateCardDetailResponse>(`/rate-cards/${rateCardId}`);
  },
  updateRateCardItem(rateCardId: string, itemId: string, body: { rate: number }) {
    return apiRequest<RateCardDetailResponse>(`/rate-cards/${rateCardId}/items/${itemId}`, {
      method: "PATCH",
      body
    });
  },
  removeRateCardItem(rateCardId: string, itemId: string) {
    return apiRequest<RateCardDetailResponse>(`/rate-cards/${rateCardId}/items/${itemId}`, {
      method: "DELETE"
    });
  },
  getRateCardDiscussion(rateCardId: string) {
    return apiRequest<RateCardDiscussionResponse>(`/rate-cards/${rateCardId}/discussion/messages`);
  },
  addRateCardDiscussionMessage(rateCardId: string, body: { message_text?: string | null; file_upload_ids?: string[] }) {
    return apiRequest<RateCardDiscussionResponse>(`/rate-cards/${rateCardId}/discussion/messages`, {
      method: "POST",
      body
    });
  },
  sendRateCardProposal(rateCardId: string, body?: { proposal_note?: string | null }) {
    return apiRequest<RateCardDetailResponse>(`/rate-cards/${rateCardId}/proposal`, {
      method: "POST",
      body: body ?? {}
    });
  },
  acceptRateCardProposal(rateCardId: string) {
    return apiRequest<RateCardDetailResponse>(`/rate-cards/${rateCardId}/proposal/accept`, { method: "POST" });
  },
  rejectRateCardProposal(rateCardId: string) {
    return apiRequest<RateCardDetailResponse>(`/rate-cards/${rateCardId}/proposal/reject`, { method: "POST" });
  },
  sendRateCardCounterProposal(
    rateCardId: string,
    body: { proposal_note?: string | null; item_updates: { item_id: string; rate: number }[] }
  ) {
    return apiRequest<RateCardDetailResponse>(`/rate-cards/${rateCardId}/proposal/counter`, {
      method: "POST",
      body
    });
  },
  initiateFileUpload(body: {
    app_flow: string;
    original_file_name: string;
    mime_type: string;
    file_size_bytes: number;
  }) {
    return apiRequest<FileUploadInitResponse>("/file-uploads", { method: "POST", body });
  },
  completeFileUpload(fileUploadId: string) {
    return apiRequest<FileUploadDetailsResponse>(`/file-uploads/${fileUploadId}/complete`, { method: "POST" });
  }
};
